import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { formatByte, isValidCustomer, showToast } from "../../../helpers/utils/Utils";
import Form from "../../../components/common/form/Form";
import Modal from "../../../components/common/form/Modal";
import InputField from "../../../components/common/form/InputField";
import { useUtilDataQuery } from "../../profile/profileApi";
import CommonSelectDropDownTag from "../../../components/common/datatable/filter/CommonSelectDropDownTag";
import { useUploadDocumentMutation, useUploadMultipleDocumentMutation } from "../orderFilesApi";
import ButtonTray from "../../../components/common/form/ButtonTray";


interface DocumentUploadSectionProps {
    show: boolean;
    setShow: (show: boolean) => void;
    EnumType?: any;
    customerOrderNumber?: any;
    documentTypeList?: any;
    orderSource?: any;
    tgxFileType?: any;
    fileDataTypeDefault?: any;
    relationalId?: any;
    actions?: any;
    orderDTO?: any;
    isOrderUpload?: any;
}

interface FileItem {
    customerOrderNumber?: string;
    visibility?: boolean;
    fileId: string;
    customerId?: string;
    fileType?: string;
    tenant?: string;
    orderId?: string;
    filename: string;
    file: File;
    fileSource?: string;
    source?: string;
    relationalId?: string;
    tgxFileType?: string;
    fileDataType?: string;
    propertyId?: string;
}

interface UploadResponse {
    success: boolean;
    message: string;
    fileName: string | null;
    className: string;
}

interface AccountNumberEntry {
    value: string;
    constant: string;
}

export default function DocumentUploadSection({
    show,
    setShow,
    EnumType = null,
    customerOrderNumber = null,
    documentTypeList = null,
    orderSource = null,
    tgxFileType = null,
    fileDataTypeDefault = null,
    relationalId,
    actions = [],
    orderDTO = null,
    isOrderUpload = false
}: DocumentUploadSectionProps) {

    const storageData = useSelector((state: any) => state.utilDataReducer);

    const dispatch = useDispatch();

    const { data: enums } = useGetEnumsQuery("");
    const [render, setRender] = useState<number | null>(null);
    const [disable, setDisable] = useState(false);
    const [isValidSubmission, setIsValidSubmission] = useState<any | null>(null);
    const [title, setTitle] = useState("");
    const [hideDelete, setHideDelete] = useState(false);
    const [bounds, setBounds] = useState({ left: 0, right: 0 });
    const draggleRef = useRef<HTMLDivElement>(null);
    const [fileList, setFileList] = useState<FileItem[]>([]);
    const [uploadedFilesResponse, setUploadedFilesResponse] = useState<UploadResponse[]>([]);
    const [uploadFileService, uploadFileServiceApi] = useUploadDocumentMutation();
    const [uploadMultiFileService, uploadMultiFileServiceApi] = useUploadMultipleDocumentMutation();

    const [responseArray, setResponseArray] = useState<UploadResponse[]>([]);
    const userProfile = useUtilDataQuery("");
    const buildAccountNumberEntry = (item: any): AccountNumberEntry => {
        return {
            value: item.propertyResult.taxId,
            constant: item.propertyResult.taxId
        }
    };

    const [accountNumbers, setAccountNumbers] = useState<AccountNumberEntry[]>(
        orderDTO?.properties
            ?.filter((cur: any) => cur?.propertyResult?.taxId)
            ?.map(buildAccountNumberEntry) ?? []
    );

    useEffect(() => {
        localStorage.setItem('cancelClicked', 'false');
    }, []);

    useEffect(() => {
        return () => {
            actions.forEach((action: any) => {
                isOrderUpload ?
                    dispatch(action(relationalId, documentTypeList))
                    :
                    dispatch(action(relationalId))
            });
        };
    }, [dispatch]);

    function handleMultipleFilesUpload() {
        let duplicateFileList: string[] = [];

        const uploadFiles = (document.getElementById('upload_doc') as HTMLInputElement).files;

        let files: (FileItem | null)[] = userProfile.data && Array.from(uploadFiles || []).map((file, index) => {
            if (computeIfFileAlreadyExists(file.name)) {
                duplicateFileList.push(file.name);
                return null;
            }

            return isOrderUpload
                ?
                {
                    customerOrderNumber: customerOrderNumber,
                    visibility: false,
                    fileId: file.name + index.toString(),
                    customerId: orderDTO?.customerDTO?.id,
                    fileType: "Other",
                    tenant: orderDTO?.tenant,
                    orderId: relationalId,
                    filename: file.name,
                    file: file,
                    fileSource: storageData?.items?.customerLevel === "CUSTOMER" ? "CUST" : "WebUI",
                    source: orderSource
                }
                :
                {
                    relationalId: relationalId,
                    fileId: file.name + index.toString(),
                    filename: file.name,
                    file: file,
                    tgxFileType: tgxFileType,
                    fileDataType: fileDataTypeDefault,
                }
        });

        if (duplicateFileList.length > 0 && duplicateFileList.length === uploadFiles?.length) {
            showToast("Files with name already Exists : " + _.join(duplicateFileList, ','));
            return;
        }

        if (Array.isArray(uploadedFilesResponse) && uploadedFilesResponse.length > 0) {
            uploadMultiFileService({
                uploadFilesDto: null, orderId: null
            })
            uploadFileService({ uploadFilesDto: null, orderId: null })
            setFileList([]);
            setUploadedFilesResponse([]);
        }


        setFileList(prevState => [
            ...prevState,
            ...((Array.isArray(files) ? files?.filter(cur => cur !== null) : []) as FileItem[]),
        ]);

        if (duplicateFileList.length > 0) {
            showToast("Files with name already Exists : " + _.join(duplicateFileList, ','));
        }
    }

    function removeFile(e: React.MouseEvent, fileId: string) {
        e.preventDefault();
        const updatedFiles = fileList.filter((file: any) => file !== null && file.fileId !== fileId);
        setFileList(updatedFiles);
    }

    function handleSingleFileChange(e: React.ChangeEvent<HTMLInputElement>, index: number, fileId: string) {
        e.preventDefault();
        const uploadFile = (document.getElementById('doc' + fileId) as HTMLInputElement).files?.[0];

        if (!uploadFile) return;

        if (computeIfFileAlreadyExists(uploadFile.name)) {
            showToast("Selected File Already Exists!!!");
            return;
        }

        const newFileList = _.map(fileList, (obj) => {
            if (obj.fileId === fileId) {
                return _.assign({}, obj, {
                    file: uploadFile,
                    filename: uploadFile.name,
                    fileId: uploadFile.name + index.toString()
                });
            }
            return obj;
        });
        setFileList(newFileList);
    }

    function computeIfFileAlreadyExists(fileName: string): boolean {
        return Array.isArray(fileList) && fileList.length > 0 && fileList.filter(c => c !== null).findIndex(cFile => cFile.filename === fileName) > -1;
    }

    function dismissModal(e: React.MouseEvent) {
        e.preventDefault();
        uploadMultiFileService({
            uploadFilesDto: null, orderId: null
        })
        uploadFileService({ uploadFilesDto: null, orderId: null })

        localStorage.setItem('cancelClicked', 'true');
        setShow(false);
    }

    useEffect(() => {
        if (responseArray && Array.isArray(responseArray) && responseArray.length > 0) {
            setUploadedFilesResponse(responseArray);
        }
    }, [responseArray]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setDisable(true);

        if (fileList.length > 0) {
            if (tgxFileType === "COLLECTOR_FILE" && fileList.findIndex((cur: any) => (cur.fileDataType === 'TAX_ROLL' || cur.fileDataType === 'APPRAISAL_ROLL') && cur.filename.slice(-3).toUpperCase() !== 'ZIP') > -1) {
                fileList.forEach((file: any) => {
                    const { fileDataType, filename } = file;
                    if ((fileDataType === 'TAX_ROLL' || fileDataType === 'APPRAISAL_ROLL') && filename.slice(-3).toUpperCase() !== 'ZIP') {
                        showToast("Only ZIP files are acceptable for file type " + fileDataType);
                    }
                });
                setDisable(false);
                return;
            }

            if (userProfile.data) {
                if (isOrderUpload) {
                    const requestBodies = fileList
                        .filter((c: any) => c !== null)
                        .map((file: any) => ({
                            customerOrderNumber,
                            visibility: userProfile.data?.customerLevel === "CUSTOMER" ? true : file.visibility,
                            fileType: file.fileType,
                            customerId: orderDTO?.customerDTO?.id,
                            tenant: orderDTO?.tenant,
                            orderId: relationalId,
                            filename: file.filename,
                            file: file.file,
                            fileSource: userProfile.data?.customerLevel === "CUSTOMER" ? "CUST" : "WebUI",
                            source: orderSource,
                        }));

                    setUploadedFilesResponse([]);
                    handleUploadFile(requestBodies)
                } else {
                    fileList.forEach((file: any) => {
                        const defaultChunkSize = userProfile.data?.chunkSize; // default chunk size in MB
                        let calculatedChunkSize = Math.ceil(defaultChunkSize / fileList.length); // calculate desired chunk size based on number of files
                        let desiredChunkSize = Math.max(3, calculatedChunkSize); // set minimum chunk size to 3 MB
                        const chunkSize = Math.min(desiredChunkSize, defaultChunkSize) * 1024 * 1024; // set chunk size based on desired and default chunk size
                        const fileSize = file.file.size;
                        const chunkCount = Math.ceil(fileSize / chunkSize);

                        const chunkList: Blob[] = [];
                        for (let j = 0; j < chunkCount; j++) {
                            const start = j * chunkSize;
                            const end = Math.min(start + chunkSize, fileSize);
                            const chunk = file.file.slice(start, end);
                            chunkList.push(chunk);
                        }

                        try {
                            const requestBodies: any[] = [];
                            chunkList.forEach((element, index) => {
                                const obj = {
                                    relationalId: relationalId,
                                    filename: file.file.name,
                                    file: element,
                                    tgxFileType: tgxFileType,
                                    fileDataType: file.fileDataType,
                                    totalChunks: chunkList.length,
                                    chunkIndex: index,
                                }
                                requestBodies.push(obj);
                            })
                            setUploadedFilesResponse([]);
                            setHideDelete(true);

                            handleUploadMultipleFile(requestBodies)
                        } catch (error) {
                            console.error('Error uploading chunks:', error);
                        }
                    })
                }
            }
        }
    }



    function fileUpLoadStatus(fileName: string): UploadResponse {
        let returnVal = uploadedFilesResponse.find(cur => cur?.fileName !== null && cur?.fileName.toString().trim() === fileName?.toString().trim());

        if (returnVal === null || returnVal === undefined) {
            return {
                success: true,
                message: "uploading!",
                fileName: null,
                className: "disableSelection fa fa-spin fa-sync large_icon ml-2"
            }
        }

        return {
            ...returnVal,
            className: returnVal.success
                ? "text-[green] fa fa-check-circle ml-2 large_icon"
                : "text-[red] fa fa-times-circle ml-2 large_icon"
        };
    }
    const handleUploadFile = async (uploadFilesDto: any) => {
        for (let i = 0; i < uploadFilesDto.length; i++) {
            const formData = new FormData();
            formData.append('file', uploadFilesDto[i].file);
            delete uploadFilesDto[i].file;
            formData.append('orderFilesData', JSON.stringify(uploadFilesDto[i]));
            const response = await uploadFileService({ orderId: relationalId, formData: formData }).unwrap();
            if (response?.multiUploadResponse?.success) {
                showToast(response?.multiUploadResponse?.message, "success")
            }
            else if (!response?.multiUploadResponse?.success && response?.message === "Success") {
                showToast(response?.multiUploadResponse?.message, "error")
            }
            else {
                showToast(response?.message, "error")
            }
            setResponseArray(previousState => {
                return [...previousState, response.multiUploadResponse]
            });
        }
    };
    const handleUploadMultipleFile = async (uploadFilesDto: any) => {
        for (let i = 0; i < uploadFilesDto.length; i++) {
            const formData = new FormData();
            formData.append('file', uploadFilesDto[i].file);

            formData.append('tgxFilesData', JSON.stringify(uploadFilesDto[i]));

            const response = await uploadMultiFileService({ orderId: relationalId, formData: formData }).unwrap();
            if (response?.multiUploadResponse?.success) {
                showToast(response?.multiUploadResponse?.message, "success")
            }
            else if (!response?.multiUploadResponse?.success && response?.message === "Success") {
                showToast(response?.multiUploadResponse?.message, "error")
            }
            else {
                showToast(response?.message, "error")
            }
            setResponseArray(previousState => {
                return [...previousState, response.multiUploadResponse]
            });
        }
    };

    return (
        <Modal
            title="Upload Document"
            show={show}
            setShow={setShow}
            className="w-3/6"
            fields={<>

                <Form onSubmit={handleSubmit} fields={
                    <>
                        <div className="modal-body">
                            <div>
                                <div className="row mt-2">

                                    <div className="col-sm-9 mt-2">
                                        <InputField className="form-control file-field" type="file" multipleFiles={true} name="file"
                                            onChange={handleMultipleFilesUpload}
                                            label="Files" id="upload_doc"
                                            required={!(fileList && Array.isArray(fileList) && fileList.length > 0)}
                                        />
                                    </div>
                                </div>
                            </div>
                            {fileList && Array.isArray(fileList) && fileList.length > 0 &&
                                <div className={"mt-4 table_data"}>
                                    <table className={'w-full mb-4 text-text-7 border-[collapse] border border-surface-5 text-left '}>
                                        <thead className="text-[white] relative bg-highlight-5">
                                            <tr>
                                                <th></th>
                                                <th>Files</th>
                                                {EnumType !== null && <th>File Type</th>}
                                                {isOrderUpload && <th>Account Number</th>}
                                                {userProfile?.data !== null && userProfile?.data !== undefined && isValidCustomer(userProfile) && isOrderUpload &&
                                                    <th> Visibility </th>}
                                            </tr>
                                        </thead>

                                        {Array.isArray(fileList) && fileList.length > 0 && fileList.map((cur, ind) => (
                                            <tr key={cur.fileId}>
                                                <td className={"align-middle text-center border border-surface-5 px-3 py-2"}>
                                                    <div
                                                        title={Array.isArray(uploadedFilesResponse) && uploadedFilesResponse.length > 0 && fileUpLoadStatus(cur?.filename)?.message || "Internal server error!"}
                                                        className={"d-flex"}>
                                                        {!hideDelete &&
                                                            <i className={"fa fa-trash mr-2 large_icon"}
                                                                onClick={(e) => removeFile(e, cur.fileId)} />
                                                        }
                                                        <i
                                                            className={
                                                                Array.isArray(uploadedFilesResponse) && uploadedFilesResponse.length > 0
                                                                    ? fileUpLoadStatus(cur?.filename)?.className
                                                                    : undefined
                                                            }
                                                        />                                                    </div>
                                                </td>

                                                <td className="border border-surface-5 px-3 py-2">
                                                    <InputField className="form-control file-field"
                                                        type="file" name="file"
                                                        onChange={(e) => handleSingleFileChange(e, ind, cur.fileId)}
                                                        id={"doc" + cur.fileId}
                                                        disabled={(responseArray && Array.isArray(responseArray) && responseArray.length > 0)}
                                                        accept={(cur?.fileDataType === 'TAX_ROLL' || cur?.fileDataType === 'APPRAISAL_ROLL') ? ".zip,.rar,.7zip" : ""}
                                                        hideLabel
                                                    />
                                                    <span
                                                        title={cur?.filename}>{(cur?.filename.length > 20 ? cur?.filename.substring(0, 20) + '...' : cur?.filename) + " (" + formatByte(cur.file.size, 1) + ") "}</span>
                                                </td>

                                                {EnumType !== null &&
                                                    <td className="border border-surface-5 px-3 py-2">
                                                        <CommonSelectDropDownTag
                                                            itemMap={enums?.[EnumType] || []}
                                                            label="constant"
                                                            value="constant"
                                                            placeholderText="Select File Type..."
                                                            isMulti={false}
                                                            name={isOrderUpload ? fileList[ind].fileType : fileList[ind].fileDataType}
                                                            setName={(value: any) => {
                                                                if (isOrderUpload) {
                                                                    fileList[ind].fileType = value;

                                                                    if (!value) {
                                                                        fileList[ind].propertyId = "";
                                                                    } else if (value === "CADPrintout") {
                                                                        setIsValidSubmission(false);
                                                                        if (accountNumbers.length < 1) {
                                                                            setTitle("Account Number is Required");
                                                                        }
                                                                    } else {
                                                                        setIsValidSubmission(true);
                                                                        fileList[ind].propertyId = "";
                                                                    }
                                                                } else {
                                                                    fileList[ind].fileDataType = value;
                                                                }

                                                                // Force re-render
                                                                setRender(new Date().getMilliseconds());
                                                            }}
                                                            isClearable={true}
                                                            isRequired={true}
                                                            isDisabled={false}
                                                        />

                                                    </td>}

                                                {
                                                    isOrderUpload && <td className="border border-surface-5 px-3 py-2">
                                                        <CommonSelectDropDownTag
                                                            itemMap={Array.isArray(accountNumbers) ? accountNumbers : []}
                                                            label="constant"
                                                            value="value"
                                                            placeholderText="Select account..."
                                                            isMulti={false}
                                                            name={fileList[ind].propertyId}
                                                            setName={(value: any) => {
                                                                fileList[ind].propertyId = value;

                                                                if (value) {
                                                                    setIsValidSubmission(true);
                                                                } else {
                                                                    setIsValidSubmission(false);
                                                                }

                                                                if (fileList[ind].fileType === "CADPrintout") {
                                                                    const filenameParts = fileList[ind].filename?.split(".");
                                                                    const extension = filenameParts?.pop();
                                                                    fileList[ind].filename = `${customerOrderNumber}_${value}_CAD_PRINTOUT.${extension}`;
                                                                }

                                                                setRender(new Date().getMilliseconds());
                                                            }}
                                                            isDisabled={fileList[ind].fileType !== "CADPrintout"}
                                                            isRequired={fileList[ind].fileType === "CADPrintout"}
                                                            isClearable={true}
                                                        />

                                                    </td>
                                                }


                                                {isOrderUpload &&
                                                    <td>
                                                        {userProfile.data !== null && userProfile.data !== undefined && isValidCustomer(userProfile) &&
                                                            <div className="text-center"
                                                                onClick={() => {
                                                                    fileList[ind]['visibility'] = !fileList[ind]['visibility'];
                                                                    setRender(new Date().getMilliseconds());
                                                                }}><i
                                                                    className={fileList[ind]['visibility'] === true ? "fa fa-eye fa-icon-color" : "fa fa-eye-slash fa-icon-color"}
                                                                    aria-hidden="true"></i></div>
                                                        }
                                                    </td>
                                                }
                                            </tr>
                                        ))}
                                    </table>
                                </div>
                            }
                        </div>
                        <div className="mt-4 border-t border-t-surface-5 flex justify-end">

                            {Array.isArray(uploadedFilesResponse) && uploadedFilesResponse.length > 0
                                && uploadedFilesResponse.length === fileList.length ?
                                <ButtonTray
                                    buttons={[
                                        {
                                            buttonProps: {
                                                btnText: "Continue",
                                                type: "submit",
                                                onClick: () => setShow(false),
                                            },
                                            buttonType: "button"
                                        },
                                    ]}
                                    align={"start"}
                                    gap={2}

                                />
                                :
                                <ButtonTray
                                    buttons={[
                                        {
                                            buttonProps: {
                                                btnText: "Cancel",
                                                onClick: () => setShow(false),
                                                type: "cancel",
                                            },
                                            buttonType: "button"
                                        },
                                        {
                                            buttonProps: {
                                                btnText: "Upload",
                                                type: "submit",
                                                isLoading: uploadFileServiceApi.isLoading || uploadMultiFileServiceApi.isLoading,
                                                isDisabled: disable,

                                            },

                                            buttonType: "button"
                                        },
                                    ]}
                                    align={"start"}
                                    gap={2}

                                />}

                        </div>

                    </>
                } />
            </>
            }
        />
    )
}